import {
  DensityUnits as PrismaDensityUnits,
  UnitType as PrismaUnitType,
} from "encamp-prisma/generated/browser";
import { convert } from "encamp-shared/src/units/unitConversions";
import { safeParseFloat } from "encamp-shared/src/utils/safeParseNumber";

export type ConvertibleAmounts = {
  maxAmount?: number | string | null;
  averageAmount?: number | string | null;
  maxAmountLargestContainer?: number | string | null;
  unit?: string | null;
};

export type ChemicalDensity =
  | { density?: number | null; densityUnits?: string | null }
  | null
  | undefined;

export type AmountConversionResult<T extends ConvertibleAmounts> = {
  value: T;
  /** false = amounts were left as-is (relabel fallback); warn the user to verify them */
  converted: boolean;
};

const toConversionUnit = (unit: string | null | undefined): PrismaUnitType | null => {
  if (!unit) return null;
  return Object.values(PrismaUnitType).find((u) => u === unit) ?? null;
};

const toConversionDensityUnits = (units: string | null | undefined): PrismaDensityUnits | null => {
  if (!units) return null;
  return Object.values(PrismaDensityUnits).find((u) => u === units) ?? null;
};

const toNumericOrNull = (value: number | string | null | undefined): number | null => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  return safeParseFloat(value);
};

// Converted values round-trip through text inputs; trim floating-point noise
// (e.g. 42*3 = 125.99999999999999 -> 126) without corrupting small amounts or
// drifting on round-trips. Significant-digit rounding preserves tiny values
// (0.4 lb -> tons stays non-zero, not flattened to 0) and, at 9 significant
// digits — matching the precision of the conversion constants themselves — lets
// lb -> kg -> lb land back on exactly 100 instead of e.g. 99.999.
const roundAmount = (value: number): number => {
  if (!Number.isFinite(value)) return value;
  return Number(value.toPrecision(9));
};

const hasAnyAmount = (item: ConvertibleAmounts): boolean =>
  toNumericOrNull(item.maxAmount) != null ||
  toNumericOrNull(item.averageAmount) != null ||
  toNumericOrNull(item.maxAmountLargestContainer) != null;

const hasUsableDensity = (chemical: ChemicalDensity): boolean => {
  const density = chemical?.density ?? null;
  const densityUnits = toConversionDensityUnits(chemical?.densityUnits);
  return density != null && density !== 0 && densityUnits != null;
};

export function convertAmountsForUnitChange<T extends ConvertibleAmounts>(
  item: T,
  fromUnit: string | null | undefined,
  toUnit: string,
  chemical: ChemicalDensity
): AmountConversionResult<T> {
  const from = toConversionUnit(fromUnit);
  const to = toConversionUnit(toUnit);

  // Nothing meaningful to convert from/to — treat as a clean no-op.
  if (!from || !to || from === to) {
    return { value: item, converted: true };
  }

  const density = chemical?.density ?? null;
  const densityUnits = toConversionDensityUnits(chemical?.densityUnits);

  // Attempt the real conversion and only fall back when it genuinely fails.
  // `convert` handles same-dimension and pure-volume changes (e.g. barrels ->
  // gallons) without a density; a missing-density mass<->volume change throws
  // or returns null, which the catch below turns into { converted: false } so
  // the row is left truthful rather than relabeled.
  const convertOne = (value: number | string | null | undefined): number | null => {
    const numeric = toNumericOrNull(value);
    if (numeric == null) return null;
    const result = convert(numeric, from, to, density, densityUnits);
    if (result == null || Number.isNaN(result)) {
      throw new Error("conversion failed");
    }
    return roundAmount(result);
  };

  try {
    return {
      value: {
        ...item,
        maxAmount: convertOne(item.maxAmount),
        averageAmount: convertOne(item.averageAmount),
        maxAmountLargestContainer: convertOne(item.maxAmountLargestContainer),
      },
      converted: true,
    };
  } catch {
    return { value: item, converted: false };
  }
}

export function healStorageLocationUnits<T extends ConvertibleAmounts>(
  items: T[],
  parentUnit: string,
  chemical: ChemicalDensity
): { storageLocations: T[]; healedCount: number; failedCount: number } {
  let healedCount = 0;
  let failedCount = 0;
  const storageLocations = items.map((item) => {
    if (!item.unit || item.unit === parentUnit) return item;
    const result = convertAmountsForUnitChange(item, item.unit, parentUnit, chemical);
    if (!result.converted) {
      // Can't convert (no density for a weight<->volume change): leave the row
      // in its own unit rather than relabeling it and silently changing what the
      // amount means (100 gallons is not 100 pounds). The
      // "storage unit must match chemical unit" rule then surfaces the mismatch.
      failedCount += 1;
      return item;
    }
    healedCount += 1;
    return { ...result.value, unit: parentUnit };
  });
  return { storageLocations, healedCount, failedCount };
}

/**
 * Target units that must not be offered as the chemical unit because converting
 * the existing storage-location amounts into them would need a density this
 * chemical doesn't have. When the chemical has usable density — or no storage
 * location carries an amount yet — nothing is blocked. Used to disable the
 * parent-unit dropdown options so a weight<->volume change can never silently
 * relabel amounts; the user is pointed to add a density first.
 */
export function unitsBlockedForMissingDensity<T extends ConvertibleAmounts>(
  storageLocations: T[],
  chemical: ChemicalDensity,
  candidateUnits: string[]
): Set<string> {
  if (hasUsableDensity(chemical)) return new Set();
  const rows = storageLocations.filter((sl) => !!sl.unit && hasAnyAmount(sl));
  if (rows.length === 0) return new Set();

  const blocked = new Set<string>();
  for (const candidate of candidateUnits) {
    const to = toConversionUnit(candidate);
    if (!to) continue;
    // A target is blocked iff actually converting some row's amounts into it
    // would fail for a missing density. Reusing the real convert path (rather
    // than canConvertWithoutDensity) means pure-volume changes such as barrels
    // -> gallons are NOT blocked, while barrels -> pounds still is.
    const needsDensity = rows.some(
      (sl) => convertAmountsForUnitChange(sl, sl.unit, candidate, chemical).converted === false
    );
    if (needsDensity) blocked.add(candidate);
  }
  return blocked;
}
